import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Drawer } from '@material-tailwind/react';
import { CloseButton, PrimaryButton, SecondaryButton } from '../../components/Buttons';
import CustomInput from '../../components/CustomInput';
import axiosInstance from '../../services/api/axios';
import { useUser } from '../../context/UserContext';
import moment from 'moment';
import { toast } from 'react-toastify';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import { ArrowBack } from '@mui/icons-material';
import { ArrowBigLeft, ArrowLeft } from 'lucide-react';


function LeadDetailsDrawer({ open, onClose, user }) {
  const [offlineComment, setOfflineComment] = useState('');
  const { userInfo } = useUser();
  const [lead, setLead] = useState(null);
  const [comments, setComments] = useState([]);
  const commentsContainerRef = useRef(null);
  const [selectedTab, setSelectedTab] = useState(0);

  // New states for extend functionality
  const [isExtendModalOpen, setIsExtendModalOpen] = useState(false);
  const [extendReason, setExtendReason] = useState('');
  const [extendDays, setExtendDays] = useState(7);
  const [isExtending, setIsExtending] = useState(false);
  const [extensionHistory, setExtensionHistory] = useState([]);
  const [currentModalStep, setCurrentModalStep] = useState('extend'); // 'extend' or 'history'

  console.log(user, "-------------");


  // MODIFIED: Handle different data structures - the user data might be in visitData.user or directly in visitData
  useEffect(() => {
    if (user) {

      const userData = user.user_account?.user || user.visitData?.user;


      setLead(userData);

      // Get comments from the correct path
      const commentDict = user.user_account?.comment || user.visitData?.user?.comment || {};
      console.log(commentDict, "-------------");
      if (commentDict) {
        const commentArray = Object.keys(commentDict)
          .sort((a, b) => parseInt(a) - parseInt(b))
          .map((key) => commentDict[key]);
        setComments(commentArray);
      } else {
        setComments([]);
      }
    }
  }, [user]);

  // Function definitions (moved before useEffect)
  const closeExtendModal = useCallback(() => {
    setIsExtendModalOpen(false);
    setCurrentModalStep('extend');
    setExtendReason('');
    setExtendDays(7);
    setExtensionHistory([]);
  }, []);

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscapeKey = (e) => {
      if (e.key === 'Escape' && isExtendModalOpen) {
        closeExtendModal();
      }
    };

    document.addEventListener('keydown', handleEscapeKey);

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isExtendModalOpen, closeExtendModal]);

  if (!lead) {
    return null;
  }

  const handleCommentChange = (event) => {
    setOfflineComment(event.target.value);
  };

  const handleSaveComment = async () => {

    const newComment = {
      description: offlineComment,
      author: userInfo.name,
      timestamp: new Date().toISOString(),
    };

    // Create the updated array of comments
    const updatedCommentsArray = [...comments, newComment];
    console.log(updatedCommentsArray, "-------------");

    // Transform the array into a dictionary
    const commentsDictionary = {};
    updatedCommentsArray.forEach((comment, index) => {
      commentsDictionary[index] = comment;
    });

    // Prepare update data
    const updateData = {
      projectId: user.user_account?.project?.projectId,
      personaId: user.user_account?.persona?.personaId,
      customerId: user.user_account?.customer?.customerId,
      userId: user.user_account?.user?.userId,
      comment: commentsDictionary,
      status: "CLOSED",
    };

    try {
      await axiosInstance.put('/customers/user', updateData);
      //console.log('Comment saved successfully!');
      setOfflineComment('');
      setComments(updatedCommentsArray);
      toast.success('Comment saved successfully!');
    } catch (error) {
      console.error('Error saving comment:', error);
      toast.error('Error saving comment');
    }
  };

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  // Handle extend functionality
  const handleExtendClick = () => {
    setCurrentModalStep('extend');
    setIsExtendModalOpen(true);
    setExtendReason('');
    setExtendDays(7);
  };

  const handleExtendSubmit = async () => {
    if (!extendReason.trim()) {
      toast.error('Please provide a reason for extension');
      return;
    }

    setIsExtending(true);
    try {
      const extendData = {
        userId: user.user_account?.user?.userId,
        projectId: user.user_account?.project?.projectId,
        agentId: user.user_account?.agent?.customerId,
       
        extensionDays: extendDays,
        reason: extendReason,
        loginId: userInfo.loginId
      };

      const response = await axiosInstance.post('/customers/cp/extend-tag', extendData);
      
      // Update the user object with the response data if available
      if (response && response.data) {
        // Update the agentExpiresAt with the new expiry date
        if (response.data.agentExpiresAt) {
          user.user_account.agentExpiresAt = response.data.agentExpiresAt;
        }
        
        // Update extension history if provided
        if (response.data.extensionReasonHistory) {
          user.user_account.extensionReasonHistory = response.data.extensionReasonHistory;
        }
      }
      
      toast.success('Lead expiry extended successfully!');
      setIsExtendModalOpen(false);
      setExtendReason('');
      setExtendDays(7);

      // Trigger a re-render by updating the lead state
      setLead(prevLead => ({ ...prevLead }));
      
    } catch (error) {
      console.error('Error extending lead:', error);
      toast.error('Failed to extend lead expiry');
    } finally {
      setIsExtending(false);
    }
  };

  const handleViewHistory = async () => {
    setCurrentModalStep('history');

    // Use existing extension history from user object
    const existingHistory = user?.user_account?.extensionReasonHistory;
    if (existingHistory) {
      const historyArray = [];
      
      // Check if it's an object with timestamp keys or a single object
      if (typeof existingHistory === 'object' && !Array.isArray(existingHistory)) {
        // Check if it has timestamp-like keys (contains colons or dashes)
        const hasTimestampKeys = Object.keys(existingHistory).some(key => 
          key.includes(':') || key.includes('-') || key.includes('T')
        );
        
        if (hasTimestampKeys) {
          // Convert object with timestamp keys to array
          Object.keys(existingHistory).forEach(key => {
            // Skip if the key is a timestamp (contains colons, dashes, or T)
            if (key.includes(':') || key.includes('-') || key.includes('T')) {
              const historyItem = existingHistory[key];
              if (historyItem && typeof historyItem === 'object') {
                historyArray.push({
                  ...historyItem,
                  timestamp: key,
                  createdAt: key
                });
              }
            }
          });
          
          // Sort by timestamp descending (newest first)
          historyArray.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        } else {
          // It's a single history object, wrap it in an array
          historyArray.push({
            ...existingHistory,
            createdAt: new Date().toISOString(),
            timestamp: new Date().toISOString()
          });
        }
      } else if (Array.isArray(existingHistory)) {
        // It's already an array
        historyArray.push(...existingHistory);
      }
      
      setExtensionHistory(historyArray);
    } else {
      setExtensionHistory([]);
    }
  };

  // Handle backdrop click to close modal
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      closeExtendModal();
    }
  };

  // Helper function to get expiry info
  const getExpiryInfo = () => {
    const agentExpiresAt = user?.user_account?.agentExpiresAt;
    if (!agentExpiresAt) return null;

    const expiryDate = moment(agentExpiresAt);
    const now = moment();
    const daysUntilExpiry = expiryDate.diff(now, 'days');
    const isExpired = daysUntilExpiry < 0;
    const isExpiringSoon = daysUntilExpiry <= 3 && daysUntilExpiry >= 0;

    return {
      date: agentExpiresAt,
      daysUntilExpiry,
      isExpired,
      isExpiringSoon,
      formattedDate: expiryDate.format('MMM DD, YYYY'),
      formattedTime: expiryDate.format('h:mm A')
    };
  };



  return (
    <>
      <Drawer open={open} onClose={onClose} placement="right" size="md">
        <Drawer.Overlay>
          <Drawer.Panel className="w-[40%] overflow-y-auto">
            <div className="flex items-center justify-between">
              <Box sx={{
                display: "flex",
                justifyContent: "center",
                borderBottom: 1,
                borderColor: "divider",
                width: "100%"
              }}>
                <Tabs
                  value={selectedTab}
                  onChange={handleTabChange}
                  centered
                >
                  <Tab
                    label="Lead Details"
                    sx={{ textTransform: 'none' }}
                  />
                  <Tab
                    label="Notification History"
                    sx={{ textTransform: 'none' }}
                  />
                </Tabs>
              </Box>
              <CloseButton onClick={onClose} />
            </div>

            <div className="p-4 overflow-y-auto">
              {selectedTab === 0 && (
                <>
                  {/* User Info */}
                  <h6 className="text-gray-700 font-semibold text-md mb-1">User Information</h6>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-md text-gray-800">
                    <div><strong>Name:</strong> {lead.firstName} {lead.lastName}</div>
                    <div><strong>Email:</strong> {lead.emailId}</div>
                    <div><strong>Phone:</strong> {lead.mobileNoWa} / {lead.mobileNoCalling || 'N/A'}</div>
                    <div>
                      <strong>Account Status:</strong>
                      {lead?.isAccountCreated ? (
                        <span className="ml-2 inline-block px-3 py-1 text-sm font-medium text-green-700 bg-green-100 rounded-full">
                          Created
                        </span>
                      ) : (
                        <span className="ml-2 inline-block px-3 py-1 text-sm font-medium text-red-700 bg-red-100 rounded-full">
                          Not Created
                        </span>
                      )}
                    </div>
                    <div>
                      <strong>Config Interested:</strong> {user.user_account?.propertyConfig?.configName || 'N/A'}
                    </div>
                    {user.user_account?.referrerUserId && (
                      <div className="flex items-center gap-2">
                        <strong>Referred By:</strong>
                        <span className="inline-flex items-center rounded-full bg-flamingo-100 px-2 py-0.5 text-xs font-medium text-flamingo-700 border border-flamingo-200">
                          Referral
                        </span>
                        <span>
                          {user.user_account?.referrer
                            ? `${user.user_account.referrer.firstName || ''} ${user.user_account.referrer.lastName || ''}`.trim() ||
                              user.user_account.referrer.emailId ||
                              user.user_account.referrer.mobileNoWa
                            : `User #${user.user_account.referrerUserId}`}
                        </span>
                      </div>
                    )}
                  </div>

                  <hr className="my-4" />

                  {/* Agent Expiry Info */}
                  {((userInfo?.persona === 'STAFF' || userInfo?.persona === 'DEVELOPER_ADMIN') || userInfo?.persona === 'DEVELOPER_ADMIN') && getExpiryInfo() && (
                    <>
                      <h6 className="text-gray-800 font-medium text-sm mb-3">Agent Assignment</h6>
                      <div className="border border-gray-200 rounded-lg p-4 mb-4">
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <span className="text-xs text-gray-500 uppercase tracking-wide">Expires</span>
                            <div className={`text-sm font-medium ${getExpiryInfo().isExpired
                                ? 'text-red-600'
                                : getExpiryInfo().isExpiringSoon
                                  ? 'text-amber-600'
                                  : 'text-gray-800'
                              }`}>
                              {getExpiryInfo().formattedDate}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className={`text-xs px-2 py-1 rounded-full ${getExpiryInfo().isExpired
                                ? 'bg-red-50 text-red-600'
                                : getExpiryInfo().isExpiringSoon
                                  ? 'bg-amber-50 text-amber-600'
                                  : 'bg-gray-50 text-gray-600'
                              }`}>
                              {getExpiryInfo().isExpired ? 'Expired' : `${getExpiryInfo().daysUntilExpiry} days left`}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={handleExtendClick}
                            className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                          >
                            Extend
                          </button>
                        </div>
                      </div>
                      <hr className="my-4" />
                    </>
                  )}

                  {/* Comments Section */}
                  <h6 className="text-gray-700 font-semibold text-base mb-2">Comments</h6>

                  <div
                    ref={commentsContainerRef}
                    className="space-y-3 overflow-y-auto max-h-48 scrollbar-thin scrollbar-thumb-rounded scrollbar-track-blue-lighter scrollbar-thumb-blue"
                  >
                    {comments && comments.length > 0 ? (
                      comments.map((comment, index) => (
                        <div key={index} className="bg-gray-100 rounded-md p-3 shadow-sm">
                          <div className="flex items-center justify-between mb-1">
                            <div className="font-semibold text-sm">{comment?.author}</div>
                            <div className="text-gray-500 text-xs">{moment(comment?.timestamp).format('MMMM Do YYYY, h:mm a')}</div>
                          </div>
                          <div className="text-gray-700">{comment?.description}</div>
                        </div>
                      ))
                    ) : (
                      <div>No comments yet.</div>
                    )}
                  </div>

                  <hr className="my-4" />

                  {/* Add Offline Comment Section */}
                  <h6 className="text-gray-700 font-semibold text-base mb-2">Add Offline Communication Notes</h6>
                  <p className="text-sm text-gray-500 mb-2">
                    Use this section to record notes from offline communications with the lead (e.g., phone calls, in-person meetings). These notes will be saved and accessible for future reference.
                  </p>
                  <textarea
                    className="w-full p-2 border border-gray-300 rounded-md mb-2"
                    rows="4"
                    placeholder="Enter your notes here..."
                    value={offlineComment}
                    onChange={handleCommentChange}
                  />
                  <PrimaryButton
                    onClick={handleSaveComment}
                  >
                    Save Comment
                  </PrimaryButton>
                </>
              )}

              {selectedTab === 1 && (
                <>
                  <h6 className="text-gray-700 font-semibold text-md mb-2">Notification History</h6>
                  {/* Display Notification History Here */}
                  <div>No notifications history yet.</div>
                </>
              )}
            </div>
          </Drawer.Panel>
        </Drawer.Overlay>
      </Drawer>

      {/* Extend Modal - Moved outside Drawer */}
      {isExtendModalOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-[9999]"
          onClick={handleBackdropClick}
        >
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full mx-4 max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h4 className="text-base font-medium text-gray-900">
                  {currentModalStep === 'extend' ? 'Extend Expiry' : 'Extension History'}
                </h4>
                <button
                  onClick={closeExtendModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-4 overflow-y-auto max-h-[60vh]">
              {currentModalStep === 'extend' ? (
                <div className="space-y-3">

                  <div className="flex items-center justify-between">
                    <label className="block text-xs font-semibold text-gray-700 ">
                      Reason <span className="text-red-500">*</span>
                    </label>
                    <button
                      type="button"
                      onClick={handleViewHistory}
                      className="ml-2 px-3 py-1.5 text-xs font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-100 transition-colors"
                    >
                      View History
                    </button>
                  </div>

                  <textarea
                    className="w-full p-3 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-gray-400 focus:border-gray-400 resize-none placeholder-gray-400"
                    rows={3}
                    placeholder="Enter reason for extension..."
                    value={extendReason}
                    onChange={(e) => setExtendReason(e.target.value)}
                  />
                  {/* Optionally, show error message below if needed */}

                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-2 uppercase tracking-wide">
                      Duration (in days)
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="365"
                      className="w-full p-3 text-sm border border-gray-200 rounded focus:ring-1 focus:ring-gray-400 focus:border-gray-400"
                      placeholder="Enter number of days"
                      value={extendDays}
                      onChange={(e) => {
                        e.stopPropagation();
                        const value = e.target.value;
                        if (value === '' || value === '0') {
                          setExtendDays('');
                        } else {
                          const numValue = parseInt(value);
                          if (!isNaN(numValue) && numValue > 0) {
                            setExtendDays(numValue);
                          }
                        }
                      }}
                      onClick={(e) => e.stopPropagation()}
                      onFocus={(e) => e.stopPropagation()}
                    />
                  </div>

                  <div className="bg-gray-50 p-3 rounded text-xs">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Current:</span>
                      <span className="font-medium">{getExpiryInfo()?.formattedDate}</span>
                    </div>
                    <div className="flex justify-between mt-1">
                      <span className="text-gray-600">New:</span>
                      <span className="font-medium">
                        {extendDays && !isNaN(extendDays) 
                          ? moment(getExpiryInfo()?.date).add(extendDays, 'days').format('MMM DD, YYYY')
                          : 'Select duration'
                        }
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                      <button
                        onClick={() => setCurrentModalStep('extend')}
                        className=" px-3 py-2 text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors flex items-left space-x-2"
                      >
                        <ArrowLeft className="size-6"/>
                      
                      </button>

                  {extensionHistory.length > 0 ? (
                    extensionHistory.map((history, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-sm font-semibold text-gray-800">
                            Extension #{index + 1}
                          </span>
                          <span className="text-xs text-gray-500 bg-white px-2 py-1 rounded">
                            {history.createdAt ? moment(history.createdAt).format('MMM DD, YYYY') : 'Recent'}
                          </span>
                        </div>
                        
                        <div className="space-y-3">
                          {/* Reason */}
                          <div className="bg-white p-3 rounded border">
                            <div className="text-xs text-gray-500 mb-1">Reason</div>
                            <div className="text-sm text-gray-800 font-medium">
                              {history.reason || 'No reason provided'}
                            </div>
                          </div>
                          
                          {/* Extension Details */}
                          <div className="grid grid-cols-2 gap-3">
                            <div className="bg-white p-3 rounded border">
                              <div className="text-xs text-gray-500 mb-1">Extension Period</div>
                              <div className="text-sm text-gray-800 font-medium">
                                {history.extensionDays || history.extension_days || 'N/A'} days
                              </div>
                            </div>
                            
                            <div className="bg-white p-3 rounded border">
                              <div className="text-xs text-gray-500 mb-1">Login ID</div>
                              <div className="text-sm text-gray-800 font-medium">
                                {history.login_id || history.loginId || 'N/A'}
                              </div>
                            </div>
                          </div>
                          
                          {/* Date Information */}
                          {history.previous_expires_at || history.previousExpiresAt ? (
                            <div className="bg-white p-3 rounded border">
                              <div className="text-xs text-gray-500 mb-2">Date Changes</div>
                              <div className="space-y-1">
                                <div className="flex justify-between text-sm">
                                  <span className="text-gray-600">Previous:</span>
                                  <span className="text-gray-800">
                                    {moment(history.previous_expires_at || history.previousExpiresAt).format('MMM DD, YYYY')}
                                  </span>
                                </div>
                                <div className="flex justify-between text-sm">
                                  <span className="text-gray-600">New:</span>
                                  <span className="text-gray-800 font-medium">
                                    {history.new_expires_at || history.newExpiresAt 
                                      ? moment(history.new_expires_at || history.newExpiresAt).format('MMM DD, YYYY')
                                      : moment(history.previous_expires_at || history.previousExpiresAt)
                                          .add(history.extensionDays || history.extension_days || 0, 'days')
                                          .format('MMM DD, YYYY')
                                    }
                                  </span>
                                </div>
                              </div>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                      <div className="text-gray-400 mb-2">
                        <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <p className="text-gray-500 text-sm font-medium">No Extension History</p>
                      <p className="text-gray-400 text-xs mt-1">No extensions have been made for this lead yet</p>
                    </div>
                  )}

                 
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end space-x-2 px-6 py-4 border-t border-gray-100">
              {currentModalStep === 'extend' && (
                <button
                  onClick={handleExtendSubmit}
                  disabled={isExtending || !extendReason.trim() || !extendDays || extendDays <= 0}
                  className="px-4 py-2 text-sm font-medium text-white bg-gray-800 rounded hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isExtending ? 'Extending...' : 'Extend'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default LeadDetailsDrawer;